import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Button, 
  Card, 
  Table, 
  Input, 
  Select, 
  DatePicker, 
  Row, 
  Col, 
  Tabs,
  Space,
  Tag,
  Radio,
  Checkbox,
  Typography,
  Tooltip
} from "antd";
import { 
  HomeOutlined, 
  LogoutOutlined,
  SearchOutlined,
  CalendarOutlined,
  FilterOutlined,
  FileTextOutlined,
  BarChartOutlined,
  ReloadOutlined
} from "@ant-design/icons";
import { ToastContainer, toast } from "react-toastify";
import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import fetchData, { fetchdatanotequal } from "../../functions/fetchData";
import ExportButtons from "../../components/ExportButtons/ExportButtons";
import WarehouseTopbar from "../../layout/WarehouseTopbar";
import CardComponent from "../../components/cards/CardComponent";
import "react-toastify/dist/ReactToastify.css";
import '../master/ExpenseHead.css';
import axios from 'axios';
import { getHeaders } from '../../utility/getHeader';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import logo from '../../assets/logo.png';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

// Ant Design styled constants with light gradients
const cardStyle = {
  background: 'linear-gradient(135deg, #ffffff 0%, #f5f7fa 100%)',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  border: 'none'
};

const headerGradient = {
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  borderRadius: '8px 8px 0 0',
  padding: '16px 24px',
  color: '#fff'
};

const filterButtonStyle = (isActive) => ({
  background: isActive 
    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
  color: isActive ? '#fff' : '#333',
  border: 'none',
  borderRadius: '8px',
  padding: '8px 16px',
  fontWeight: 600,
  boxShadow: isActive 
    ? '0 4px 12px rgba(102, 126, 234, 0.4)'
    : '0 2px 4px rgba(0,0,0,0.1)',
  transition: 'all 0.3s ease'
});

const tabStyle = {
  background: 'linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)',
  borderRadius: '8px',
  padding: '8px',
  marginBottom: '16px'
};

const inputStyle = {
  borderRadius: '8px',
  border: '2px solid #e1e8ed',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
};

// Minimal styles (keeping only what's absolutely needed)
const compactStyles = `
  /* Tab hover effects */
  .custom-tabs .nav-tabs li a:hover {
    background-color: #28a745 !important;
    color: white !important;
    border-color: #28a745 !important;
  }
  
  .custom-tabs .nav-tabs li.active a {
    background-color: #007bff;
    color: white;
    border-color: #007bff;
  }

  /* Compact form styling */
  .compact-report-ui .form-group {
    margin-bottom: 0.75rem;
  }

  .compact-report-ui .form-group label {
    margin-bottom: 0.25rem;
    font-size: 0.875rem;
    font-weight: 500;
  }

  .compact-report-ui .form-control-sm {
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
    line-height: 1.5;
    border-radius: 4px;
    border-width: 1px;
  }

  /* Consistent button styling */
  .compact-report-ui .btn-compact {
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
    line-height: 1.5;
    border-radius: 4px !important;
    border-width: 1px !important;
    min-width: 110px;
    font-weight: 500;
  }

  .compact-report-ui .btn-compact i {
    margin-right: 4px;
  }

  /* Compact table styling */
  .compact-report-ui .table-responsive {
    border-radius: 4px;
    border: 1px solid #dee2e6;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    pointer-events: auto;
  }

  .compact-report-ui .table {
    margin-bottom: 0;
    font-size: 0.875rem;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    table-layout: fixed;
  }

  .compact-report-ui .table th,
  .compact-report-ui .table td {
    padding: 0.5rem;
    vertical-align: middle;
    will-change: auto;
    pointer-events: auto;
  }

  .compact-report-ui .table tbody tr {
    pointer-events: none;
  }

  .compact-report-ui .table tbody tr > * {
    pointer-events: auto;
  }

  .compact-report-ui .table thead th {
    font-size: 0.8125rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  /* Compact card styling */
  .compact-report-ui .card {
    border-radius: 4px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  }

  .compact-report-ui .card-header {
    padding: 0.75rem 1rem;
    font-size: 1rem;
    font-weight: 600;
  }

  .compact-report-ui .card-body {
    padding: 1rem;
  }

  /* Pagination compact */
  .compact-report-ui .pagination {
    margin-bottom: 0;
  }

  .compact-report-ui .page-link {
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
    border-radius: 4px !important;
  }

  /* Action buttons row */
  .compact-report-ui .action-buttons-row {
    padding: 0.75rem 0;
    margin-top: 0.5rem;
    border-top: 1px solid #e9ecef;
  }

  /* Form row spacing */
  .compact-report-ui .form-row {
    margin-bottom: 0.5rem;
  }

  /* Filter Mode Buttons */
  .filter-mode-buttons {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
    margin-bottom: 15px;
    padding: 12px;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-radius: 8px;
    border: 1px solid #dee2e6;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  }

  .filter-mode-btn {
    background: linear-gradient(135deg, #FFD700 0%, #DAA520 100%);
    color: #333;
    border: 2px solid transparent;
    padding: 8px 10px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    box-shadow: 0 2px 4px rgba(218, 165, 32, 0.3), 0 1px 2px rgba(0,0,0,0.1);
    position: relative;
    overflow: hidden;
    will-change: background, box-shadow, border-color;
  }

  .filter-mode-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
    transition: left 0.5s;
  }

  .filter-mode-btn:hover::before {
    left: 100%;
  }

  .filter-mode-btn:hover {
    box-shadow: 0 6px 12px rgba(218, 165, 32, 0.4), 0 3px 6px rgba(0,0,0,0.15);
    background: linear-gradient(135deg, #FFE55C 0%, #E6B82E 100%);
    border-color: #DAA520;
  }

  .filter-mode-btn.active {
    background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
    color: white;
    border-color: #1e7e34;
    box-shadow: 0 6px 16px rgba(40,167,69,0.5), 0 3px 8px rgba(0,0,0,0.2);
  }

  .filter-mode-btn.active:hover {
    background: linear-gradient(135deg, #2ecc71 0%, #27d89f 100%);
  }

  .filter-mode-btn i {
    font-size: 16px;
    margin-bottom: 4px;
    text-shadow: 0 1px 2px rgba(0,0,0,0.1);
  }

  .filter-mode-btn span {
    font-size: 9px;
    line-height: 1.3;
    font-weight: 600;
    letter-spacing: 0.2px;
  }

  /* Search Input Styling */
  .global-search-input {
    margin-bottom: 20px;
  }

  .global-search-input input {
    border: 2px solid #DAA520;
    border-radius: 8px;
    padding: 12px 40px 12px 15px;
    font-size: 14px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }

  .global-search-input input:focus {
    border-color: #FFD700;
    box-shadow: 0 0 0 3px rgba(218, 165, 32, 0.1);
    outline: none;
  }

  .global-search-wrapper {
    position: relative;
  }

  .global-search-wrapper i {
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    color: #DAA520;
    font-size: 16px;
  }

  /* Searchable Select Styling */
  input[list] {
    border: 2px solid #ced4da;
    transition: border-color 0.3s ease;
  }

  input[list]:focus {
    border-color: #DAA520;
    box-shadow: 0 0 0 2px rgba(218, 165, 32, 0.1);
  }

  /* Date Range Filter Styling */
  .date-range-filter {
    background: linear-gradient(135deg, #fff8e1 0%, #fffbf0 100%);
    border: 2px solid #DAA520;
    border-radius: 8px;
    padding: 15px;
    margin-bottom: 15px;
  }

  .date-range-filter label {
    font-weight: 600;
    color: #B8860B;
    margin-bottom: 5px;
    font-size: 12px;
  }

  .date-range-filter input[type="date"] {
    border: 2px solid #DAA520;
    border-radius: 6px;
    transition: all 0.3s ease;
  }

  .date-range-filter input[type="date"]:focus {
    border-color: #FFD700;
    box-shadow: 0 0 0 3px rgba(218, 165, 32, 0.15);
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.type = "text/css";
  styleSheet.innerText = compactStyles;
  if (!document.head.querySelector('style[data-compact-styles]')) {
    styleSheet.setAttribute('data-compact-styles', 'true');
    document.head.appendChild(styleSheet);
  }
}


export default function StockReport() {
  // Get user type from localStorage or sessionStorage
  const userType = (localStorage.getItem('usertype') || sessionStorage.getItem('usertype') || '').toLowerCase();
  const navigate = useNavigate();
  
  const [stockData, setStockData] = useState([]); // For wstock data
  const [filteredData, setFilteredData] = useState([]);
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({
    item_name: "",
    sponsor_name: "",
    supplier_name: "",
    date: "",
    dateTo: "",
    stockType: "", // 'in', 'out', or ''
    groupByRef: false,
    ref: "",
    category: "", // Filter by category
    searchText: "" // For search filter mode
  });
  const [dateRange, setDateRange] = useState([null, null]);
  const [viewingClosingStock, setViewingClosingStock] = useState(false);
  const [showZeroClosingStock, setShowZeroClosingStock] = useState(false);
  const [stockDisplayFilter, setStockDisplayFilter] = useState('all'); // 'all', 'zero', 'nonzero'
  const [reportType, setReportType] = useState('stock'); // 'stock' or 'closingstock'
  const [filterMode, setFilterMode] = useState(''); // Filter mode for 8 filter buttons
  const [globalSearch, setGlobalSearch] = useState(''); // Global search text
  const [hideClubRows, setHideClubRows] = useState(false); // Hide Club supplier rows
  const [hideClubReturnRows, setHideClubReturnRows] = useState(false); // Hide Club_Return supplier rows
  const [invoiceSummary, setInvoiceSummary] = useState(null); // Store invoice summary data
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [editingRow, setEditingRow] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Apply all filters including hideClubRows and hideClubReturnRows
  const getDisplayedData = () => {
    let data = filteredData;
    
    // Hide Club rows if checkbox is enabled
    if (hideClubRows) {
      data = data.filter(item => item.sname && item.sname.toLowerCase() !== 'club');
    }
    
    // Hide Club_Return rows if checkbox is enabled
    if (hideClubReturnRows) {
      data = data.filter(item => item.sname && item.sname.toLowerCase() !== 'club_return');
    }
    
    return data;
  };

  const columns = viewingClosingStock || reportType === 'closingstock'
    ? [
        { label: "S.No.", field: "sno" },
        { label: "Item Name", field: "item_name" },
        { label: "Category", field: "category" },
        { label: "Quantity", field: "quantity" },
        { label: "Price", field: "price" },
        { label: "Total Amount", field: "total_amount" },
        { label: "Actions", field: "actions" },
      ]
    : filters.groupByRef
      ? [
          { label: "Date", field: "date" },
          { label: "Ref No", field: "ref" },
          { label: "Total Amount", field: "amount" },
          { label: "Actions", field: "actions" },
        ]
      : filterMode === 'date-item-group'
        ? [
            { label: "Item Name", field: "name" },
            { label: "Qty", field: "qty" },
            { label: "Rate", field: "rate" },
            { label: "Amount", field: "amount" },
            { label: "Actions", field: "actions" },
          ]
      : filterMode === 'invoice-group'
        ? [
            { label: "Invoice No", field: "invoice_number" },
            { label: "Supplier Name", field: "supplier_name" },
            { label: "Amount", field: "total_amount" },
            { label: "Actions", field: "actions" },
          ]
        : [
            { label: "Date", field: "date" },
            { label: "Item Name", field: "name" },
            { label: "Supplier", field: "sname" },
            { label: "Ref No", field: "ref" },
            { label: "Sponsor", field: "sponsor_name" },
            { label: "Qty", field: "qty" },
            { label: "Sold Qty", field: "sold_qty" },
            { label: "Rate", field: "rate" },
            { label: "Amount", field: "amount" },
            { label: "Actions", field: "actions" },
          ];

  // Handle edit action
  const handleEdit = (row) => {
    setEditingRow(row);
    toast.info(`Editing: ${row.item_name || row.name || row.invoice_number}`);
    // You can open a modal or inline editing here
    console.log('Edit row:', row);
  };

  // Handle delete action
  const handleDelete = async (row) => {
    if (deleteConfirm === row) {
      try {
        // Implement your delete API call here
        // Example: await axios.delete(`${API_URL}/wstock/${row.id}`, { headers: getHeaders() });
        
        toast.success(`Deleted: ${row.item_name || row.name || row.invoice_number}`);
        setDeleteConfirm(null);
        
        // Refresh data after delete
        const updatedData = filteredData.filter(item => item !== row);
        setFilteredData(updatedData);
      } catch (error) {
        toast.error('Failed to delete record');
        console.error('Delete error:', error);
      }
    } else {
      setDeleteConfirm(row);
      toast.warning('Click delete again to confirm');
      setTimeout(() => setDeleteConfirm(null), 3000);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        // Always fetch stock data from wstock table (used for all reports)
        await fetchData("wstock", (stockData) => {
          console.log("Stock data fetched:", stockData);
          setStockData(stockData);
          setFilteredData(stockData);
        }, "date", {}); // Sort by date for better organization
      } catch (err) {
        console.error("Data fetch error:", err);
        toast.error("Failed to fetch data");
      }

      // Fetch master data
      fetchData("items", (itemsData) => {
        console.log("Items fetched:", itemsData);
        setItems(itemsData);
      }, "id", {});
    };

    loadData();
  }, [reportType]);

  // Get unique items from data
  const getUniqueItems = () => {
    const dataSource = stockData; // All reports use stockData from wstock
    
    if (dataSource.length === 0) {
      // Fallback to items from master data
      return items.map(item => item.iname || item.name).filter(Boolean).sort();
    }
    
    const itemNames = dataSource.map(item => item.name).filter(Boolean);
    return [...new Set(itemNames)].sort();
  };

  // Get unique sponsors from stock data
  const getUniqueSponsors = () => {
    const sponsors = stockData.map(item => item.sponsor_name).filter(Boolean);
    return [...new Set(sponsors)].sort();
  };

  // Get unique suppliers from stock data
  const getUniqueSuppliers = () => {
    const suppliers = stockData.map(item => item.sname).filter(Boolean);
    return [...new Set(suppliers)].sort();
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    if (dates && dates[0] && dates[1]) {
      setFilters((prev) => ({
        ...prev,
        date: dates[0].format('YYYY-MM-DD'),
        dateTo: dates[1].format('YYYY-MM-DD')
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        date: '',
        dateTo: ''
      }));
    }
    // Re-apply filter mode if one is active
    if (filterMode) {
      setTimeout(() => handleFilterModeChange(filterMode), 0);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters((prev) => {
      const newFilters = { 
        ...prev, 
        [name]: type === 'checkbox' ? checked : value 
      };
      
      // Re-apply filter mode if one is active
      if (filterMode) {
        setTimeout(() => handleFilterModeChange(filterMode), 0);
      }
      
      return newFilters;
    });
  };

  const autoApplyFilters = useCallback(async (newFilters) => {
    // For Stock Movement Report, use server-side filtering with fetchData
    if (reportType === 'stock') {
      try {
        // Build where conditions for server-side filtering
        let whereConditions = {};
        
        // Add date filters
        if (newFilters.date) {
          whereConditions.date = newFilters.date;
        }
        if (newFilters.date) {
          whereConditions.date = newFilters.date;
        }
        
        // Add item name filter
        if (newFilters.item_name) {
          whereConditions.name = newFilters.item_name;
        }
        
        // Add sponsor name filter
        if (newFilters.sponsor_name) {
          whereConditions.sponsor_name = newFilters.sponsor_name;
        }
        
        // Add category filter
        if (newFilters.category) {
          whereConditions.category = newFilters.category;
        }
        
        // Add ref number filter
        if (newFilters.ref) {
          whereConditions.ref = newFilters.ref;
        }
        
        console.log('Fetching stock movement data with conditions:', whereConditions);
        
        let filtered;
        
        // Fetch all data first, then apply client-side filtering based on stockType
        filtered = await new Promise((resolve, reject) => {
          fetchData('wstock', (data) => {
            console.log('Fetched stock movement data:', data);
            
            // Apply stock type filter based on sname column
            if (newFilters.stockType === 'in') {
              // Stock In: exclude club and warehouse
              data = data.filter(record => 
                record.sname !== 'club' && record.sname !== 'warehouse'
              );
              console.log('Filtered for Stock In (sname != club and warehouse):', data);
            } else if (newFilters.stockType === 'out') {
              // Stock Out: only club records
              data = data.filter(record => record.sname === 'club');
              console.log('Filtered for Stock Out (sname = club):', data);
            }
            
            resolve(data);
          }, 'date DESC', whereConditions);
        });
        
        // Group by ref no and date if enabled
        if (newFilters.groupByRef) {
          const grouped = {};
          filtered.forEach(row => {
            const key = `${row.ref || 'No Ref'}_${row.date}`;
            if (!grouped[key]) {
              grouped[key] = {
                ...row,
                qty: 0,
                sold_qty: 0,
                total: 0,
                amount: 0,
                count: 0
              };
            }
            grouped[key].qty += parseFloat(row.qty || 0);
            grouped[key].sold_qty += parseFloat(row.sold_qty || 0);
            grouped[key].total += parseFloat(row.total || 0);
            grouped[key].amount += parseFloat(row.amount || 0);
            grouped[key].count += 1;
          });
          filtered = Object.values(grouped);
        }
        
        setFilteredData(filtered);
        setViewingClosingStock(false);
        setCurrentPage(1);
        return;
        
      } catch (error) {
        console.error('Error fetching filtered data:', error);
        toast.error('Error applying filters');
        setFilteredData([]);
        return;
      }
    }
    
    // For Closing Stock Report, use client-side filtering
    let dataSource = stockData;
    let filtered = [...dataSource];

    // Filter by category
    if (newFilters.category) {
      filtered = filtered.filter((row) => {
        const category = row.category || row.type || row.item_category;
        return category === newFilters.category;
      });
    }

    // Group by ref no and date if enabled (only for stock movement)
    if (newFilters.groupByRef && reportType === 'stock') {
      const grouped = {};
      filtered.forEach(row => {
        const key = `${row.ref || 'No Ref'}_${row.date}`;
        if (!grouped[key]) {
          grouped[key] = {
            ...row,
            qty: 0,
            sold_qty: 0,
            total: 0,
            amount: 0,
            count: 0
          };
        }
        grouped[key].qty += parseFloat(row.qty || 0);
        grouped[key].sold_qty += parseFloat(row.sold_qty || 0);
        grouped[key].total += parseFloat(row.total || 0);
        grouped[key].amount += parseFloat(row.amount || 0);
        grouped[key].count += 1;
      });
      filtered = Object.values(grouped);
    }

    setFilteredData(filtered);
    setViewingClosingStock(false);
    setCurrentPage(1); // Reset to first page when filters are applied
  }, [reportType, stockData]); // Updated dependencies

  // Auto-apply filters when they change
  useEffect(() => {
    if (stockData.length > 0) {
      autoApplyFilters(filters);
    }
  }, [filters, stockData, autoApplyFilters]);

  const applyFilters = async () => await autoApplyFilters(filters);

  // Handle filter mode change - now using backend API routes
  const handleFilterModeChange = async (mode) => {
    setFilterMode(mode);
    setCurrentPage(1);
    
    try {
      let apiUrl = '';
      let params = {};
      
      switch(mode) {
        case 'search':
          // Global search endpoint
          if (!globalSearch && !filters.searchText) {
            setFilteredData(stockData);
            return;
          }
          apiUrl = '/stock/search';
          params = { q: globalSearch || filters.searchText };
          break;
          
        case 'date':
          // Filter by date range
          apiUrl = '/stock/filter-by-date';
          params = {
            from: filters.date || '',
            to: filters.dateTo || ''
          };
          break;
          
        case 'date-supplier':
          // Filter by date + supplier
          apiUrl = '/stock/filter-by-date-supplier';
          params = {
            from: filters.date || '',
            to: filters.dateTo || '',
            supplier: filters.supplier_name || ''
          };
          break;
          
        case 'date-sponsor':
          // Filter by date + sponsor
          apiUrl = '/stock/filter-by-date-sponsor';
          params = {
            from: filters.date || '',
            to: filters.dateTo || '',
            sponsor: filters.sponsor_name || ''
          };
          break;
          
        case 'date-item':
          // Filter by date + item
          apiUrl = '/stock/filter-by-date-item';
          params = {
            from: filters.date || '',
            to: filters.dateTo || '',
            item: filters.item_name || ''
          };
          break;
          
        case 'date-item-group':
          // Filter by date + group by item (aggregate)
          apiUrl = '/stock/filter-group-by-item';
          params = {
            from: filters.date || '',
            to: filters.dateTo || ''
          };
          break;
          
        case 'item-supplier-date':
          // Filter by item + supplier + date (three-way)
          apiUrl = '/stock/filter-item-supplier-date';
          params = {
            from: filters.date || '',
            to: filters.dateTo || '',
            item: filters.item_name || '',
            supplier: filters.supplier_name || ''
          };
          break;
          
        case 'invoice-group':
          // Group by invoice number with date
          apiUrl = '/warehousemanager/invoice-summary';
          params = {
            from: filters.date || '',
            to: filters.dateTo || '',
            category: filters.category || ''
          };
          break;
          
        default:
          // No filter mode selected, show all data
          setFilteredData(stockData);
          return;
      }
      
      // Make API call
      const response = await axios.get(apiUrl, {
        params,
        ...getHeaders()
      });
      
      if (response.data && response.data.data) {
        // Filter out items with 0 qty/amount when grouping
        const filteredResponseData = (mode === 'date-item-group' || mode === 'invoice-group')
          ? response.data.data.filter(item => {
              if (mode === 'date-item-group') {
                return item.qty && parseFloat(item.qty) > 0;
              } else if (mode === 'invoice-group') {
                return item.total_amount && parseFloat(item.total_amount) > 0;
              }
              return true;
            })
          : response.data.data;
        
        setFilteredData(filteredResponseData);
        
        // Store summary data if available
        if (response.data.summary) {
          console.log('Invoice Summary:', response.data.summary);
          setInvoiceSummary(response.data.summary);
        }
        
        toast.success(`Filter applied successfully`);
      } else {
        setFilteredData([]);
      }
      
      
    } catch (error) {
      console.error('Error applying filter:', error);
      toast.error('Error applying filter');
      setFilteredData([]);
    }
  };

  // Function to show only stock in records (sname != 'club')
  const showStockIn = async () => {
    try {
      console.log('Fetching stock in records where sname != club');
      
      const whereConditions = {
        sname: 'club' // Backend should handle this as NOT EQUAL (exclude club)
      };
      
      const fetchedData = await fetchdatanotequal('wstock', null, 'date', whereConditions);
      console.log('Stock in data fetched:', fetchedData);
      setFilteredData(fetchedData || []);
      setCurrentPage(1);
      toast.success('Stock In records loaded (sname != club)');
    } catch (error) {
      console.error('Error fetching stock in data:', error);
      toast.error('Error loading stock in records');
    }
  };

  // Function to show only stock out records (sname === 'club')
  const showStockOut = async () => {
    try {
      console.log('Fetching stock out records where sname = club');
      
      // Use fetchData with callback and filter for club records
      const fetchedData = await new Promise((resolve, reject) => {
        fetchData('wstock', (data) => {
          // Filter to only club records
          const clubRecords = data.filter(record => record.sname === 'club');
          resolve(clubRecords);
        }, 'date DESC', {});
      });
      
      console.log('Stock out data fetched:', fetchedData);
      setFilteredData(fetchedData || []);
      setCurrentPage(1);
      toast.success('Stock Out records loaded (sname = club)');
    } catch (error) {
      console.error('Error fetching stock out data:', error);
      toast.error('Error loading stock out records');
    }
  };

  // Handle global search
  const handleGlobalSearchChange = async (e) => {
    const searchText = e.target.value;
    setGlobalSearch(searchText);
    setCurrentPage(1);
    
    // Apply global search filter using API
    if (searchText) {
      try {
        const response = await axios.get('/stock/search', {
          params: { q: searchText },
          ...getHeaders()
        });
        
        if (response.data && response.data.data) {
          setFilteredData(response.data.data);
        } else {
          setFilteredData([]);
        }
      } catch (error) {
        console.error('Error searching:', error);
        // Fallback to client-side search if API fails
        const searchLower = searchText.toLowerCase();
        const filtered = stockData.filter(item => 
          (item.name && item.name.toLowerCase().includes(searchLower)) ||
          (item.sname && item.sname.toLowerCase().includes(searchLower)) ||
          (item.sponsor_name && item.sponsor_name.toLowerCase().includes(searchLower)) ||
          (item.ref && item.ref.toLowerCase().includes(searchLower)) ||
          (item.inv && item.inv.toLowerCase().includes(searchLower)) ||
          (item.category && item.category.toLowerCase().includes(searchLower))
        );
        setFilteredData(filtered);
      }
    } else if (filterMode) {
      // Re-apply filter mode if active
      handleFilterModeChange(filterMode);
    } else {
      setFilteredData(stockData);
    }
  };

  const resetFilters = () => {
    const reset = { 
      item_name: "",
      sponsor_name: "",
      supplier_name: "",
      date: "",
      dateTo: "",
      stockType: "",
      groupByRef: false,
      ref: "",
      category: "",
      searchText: ""
    };
    setFilters(reset);
    setFilterMode(''); // Reset filter mode
    setGlobalSearch(''); // Reset global search
    setShowZeroClosingStock(false); // Reset zero closing stock checkbox
    setStockDisplayFilter('all'); // Reset stock display filter
    setFilteredData(stockData); // All reports use stockData
    setViewingClosingStock(false);
    setCurrentPage(1); // Reset to first page when filters are reset
  };

  // Pagination logic
  const displayedData = getDisplayedData();
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = displayedData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(displayedData.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const getPageNumbers = () => {
    const maxPagesToShow = 5;
    if (totalPages <= maxPagesToShow) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages = [];
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, currentPage + 2);
    if (currentPage <= 3) {
      end = maxPagesToShow;
    } else if (currentPage >= totalPages - 2) {
      start = totalPages - maxPagesToShow + 1;
    }
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  const viewClosingStock = useCallback(() => {
    let dataSource = stockData; // Always use stockData from wstock
    
    const closingStockMap = {};
    
    // Get the latest record for each item (highest ID = last inserted)
    dataSource.forEach((entry) => {
      const itemKey = entry.name;
      const itemId = entry.name;
      
      if (!closingStockMap[itemId] || closingStockMap[itemId].id < entry.id) {
        closingStockMap[itemId] = {
          ...entry,
          item_name: itemKey,
          // For wstock data, calculate closing stock based on type
          quantity: entry.qty || 0,
          price: entry.rate || 0,
          category: entry.category || entry.type || ''
        };
      }
    });

    let final = Object.values(closingStockMap).map((item, index) => ({
      sno: index + 1,
      item_name: item.item_name,
      quantity: item.quantity || 0,
      price: parseFloat(item.price || 0).toFixed(2),
      total_amount: (parseFloat(item.quantity || 0) * parseFloat(item.price || 0)).toFixed(2),
      category: item.category,
    }));

    // Apply category filter if selected
    if (filters.category) {
      final = final.filter(item => item.category === filters.category);
    }

    // Apply zero stock filter if enabled
    if (!showZeroClosingStock) {
      // Hide zero stock items (default behavior)
      final = final.filter(item => parseFloat(item.quantity) > 0);
    }
    // If checked, show all items including zero stock

    final = final.sort((a, b) => a.item_name.localeCompare(b.item_name));
    final = final.map((item, index) => ({ ...item, sno: index + 1 }));

    setFilteredData(final);
    setViewingClosingStock(true);
    setCurrentPage(1); // Reset to first page
  }, [stockData, filters.category, showZeroClosingStock]); // Updated dependencies

  const switchReportType = (type) => {
    setReportType(type);
    setViewingClosingStock(false);
    setShowZeroClosingStock(false);
    setStockDisplayFilter('all'); // Reset stock display filter
    resetFilters();
    
    // All report types use stockData from wstock table
    setFilteredData(stockData);
    
    if (type === 'closingstock') {
      // Automatically show closing stock when this tab is selected
      setTimeout(() => viewClosingStock(), 100); // Small delay to ensure state is updated
      return;
    }
    
    setCurrentPage(1); // Reset to first page when switching report types
  };

  // Function to show items with zero closing stock
  const viewZeroClosingStock = useCallback(() => {
    let dataSource = stockData;
    
    const closingStockMap = {};
    
    // Process stock data to calculate closing stock
    dataSource.forEach((item) => {
      const itemName = item.name;
      if (!itemName) return;

      if (!closingStockMap[itemName]) {
        closingStockMap[itemName] = {
          item_name: itemName,
          quantity: 0,
          price: parseFloat(item.rate || 0),
          category: item.category || item.type || item.item_category || '',
        };
      }

      if (item.type === 'in') {
        closingStockMap[itemName].quantity += parseFloat(item.qty || 0);
      } else if (item.type === 'out') {
        closingStockMap[itemName].quantity -= parseFloat(item.qty || 0);
      }
    });

    let final = Object.values(closingStockMap);
    
    // Apply stock display filter
    if (stockDisplayFilter === 'zero') {
      final = final.filter(item => item.quantity === 0); // Only zero stock items
    } else if (stockDisplayFilter === 'nonzero') {
      final = final.filter(item => item.quantity > 0); // Only non-zero stock items
    }
    // If 'all', don't filter by quantity
    
    final = final.map((item, index) => ({
      sno: index + 1,
      item_name: item.item_name,
      quantity: item.quantity,
      price: parseFloat(item.price || 0).toFixed(2),
      total_amount: (parseFloat(item.quantity || 0) * parseFloat(item.price || 0)).toFixed(2),
      category: item.category,
    }));

    // Apply category filter if selected
    if (filters.category) {
      final = final.filter(item => item.category === filters.category);
    }

    final = final.sort((a, b) => a.item_name.localeCompare(b.item_name));
    final = final.map((item, index) => ({ ...item, sno: index + 1 }));

    setFilteredData(final);
    setViewingClosingStock(true);
    setCurrentPage(1); // Reset to first page
  }, [stockData, filters.category, stockDisplayFilter]); // Updated dependencies

  // Handle stock display filter change
  useEffect(() => {
    if (viewingClosingStock) {
      // Re-apply the closing stock view whenever filters change
      if (stockDisplayFilter === 'zero') {
        viewZeroClosingStock();
      } else {
        viewClosingStock();
      }
    }
  }, [stockDisplayFilter, filters.category, viewZeroClosingStock, viewClosingStock, viewingClosingStock]);

  // Row styler for PDF export - highlights zero stock items
  const getRowStyleForExport = (row) => {
    if ((viewingClosingStock || reportType === 'closingstock') && parseFloat(row.Quantity || row.quantity || 0) === 0) {
      return {
        fillColor: [220, 53, 69], // Red color RGB
        textColor: [255, 255, 255] // White color RGB
      };
    }
    return null;
  };

  const getExportColumns = () => {
    if (viewingClosingStock || reportType === 'closingstock') {
      return [
        { key: 'sno', label: 'S.No.' },
        { key: 'item_name', label: 'Item Name' },
        { key: 'category', label: 'Category' },
        { key: 'quantity', label: 'Quantity' },
        { key: 'price', label: 'Price' },
        { key: 'total_amount', label: 'Total Amount' }
      ];
    }
    
    // Invoice Group Export
    if (filterMode === 'invoice-group') {
      return [
        { key: 'invoice_number', label: 'Invoice No' },
        { key: 'supplier_name', label: 'Supplier Name' },
        { key: 'total_amount', label: 'Amount' }
      ];
    }
    
    // Stock Movement Report
    if (filters.groupByRef) {
      return [
        { key: 'date', label: 'Date' },
        { key: 'ref', label: 'Ref No' },
        { key: 'amount', label: 'Total Amount' }
      ];
    }
    return [
      { key: 'date', label: 'Date' },
      { key: 'name', label: 'Item Name' },
      { key: 'sname', label: 'Supplier' },
      { key: 'ref', label: 'Ref No' },
      { key: 'sponsor_name', label: 'Sponsor' },
      { key: 'qty', label: 'Qty' },
      { key: 'rate', label: 'Rate' },
      { key: 'amount', label: 'Amount' }
    ];
  };

  // Custom PDF export for Stock Movement Report (filtered by date)
  const exportStockMovementPDF = () => {
    const displayData = getDisplayedData();
    
    if (!displayData || displayData.length === 0) {
      toast.warning('No data to export');
      return;
    }

    // Filter data by date range if specified
    let filteredByDate = displayData;
    if (filters.date || filters.dateTo) {
      filteredByDate = displayData.filter(item => {
        const itemDate = new Date(item.date);
        const fromDate = filters.date ? new Date(filters.date) : null;
        const toDate = filters.dateTo ? new Date(filters.dateTo) : null;
        
        if (fromDate && toDate) {
          return itemDate >= fromDate && itemDate <= toDate;
        } else if (fromDate) {
          return itemDate >= fromDate;
        } else if (toDate) {
          return itemDate <= toDate;
        }
        return true;
      });
    }

    const doc = new jsPDF('portrait', 'mm', 'a4');
    
    // Add logo with proper aspect ratio
    try {
      doc.addImage(logo, 'PNG', 10, 10, 31, 20);
    } catch (error) {
      console.log('Logo not added:', error);
    }
    
    // Add title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Stock Movement Report', 45, 20);
    
    // Add date range if filters are applied
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    let yPos = 28;
    if (filters.date || filters.dateTo) {
      const fromDate = filters.date || 'Start';
      const toDate = filters.dateTo || 'End';
      doc.text(`Period: ${fromDate} to ${toDate}`, 45, yPos);
      yPos += 6;
    }
    
    doc.text(`Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`, 45, yPos);
    
    // Prepare table data - only S.No., Item Name, and Sold Qty
    const tableData = filteredByDate.map((row, index) => [
      index + 1,
      row.name || '',
      row.sold_qty || row.qty || 0
    ]);
    
    // Calculate total sold qty
    const totalSoldQty = filteredByDate.reduce((sum, row) => {
      return sum + (parseFloat(row.sold_qty) || parseFloat(row.qty) || 0);
    }, 0);
    
    // Add total row
    tableData.push([
      { content: 'TOTAL', colSpan: 2, styles: { fontStyle: 'bold', halign: 'right' } },
      { content: totalSoldQty.toFixed(2), styles: { fontStyle: 'bold', halign: 'center' } }
    ]);
    
    // Add table
    doc.autoTable({
      startY: yPos + 8,
      head: [['S.No.', 'Item Name', 'Sold Qty']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: [44, 62, 80],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 11,
        halign: 'center'
      },
      styles: {
        fontSize: 9,
        cellPadding: 3,
        overflow: 'linebreak'
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 20 },
        1: { halign: 'left', cellWidth: 130 },
        2: { halign: 'center', cellWidth: 40 }
      },
      margin: { left: 14, right: 14 },
      didDrawPage: function (data) {
        // Footer
        const pageCount = doc.internal.getNumberOfPages();
        const pageSize = doc.internal.pageSize;
        const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
        doc.setFontSize(8);
        doc.text(`Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`, 14, pageHeight - 10);
      }
    });
    
    // Add signature section
    const finalY = doc.lastAutoTable.finalY || yPos + 8;
    const pageHeight = doc.internal.pageSize.height;
    const signatureY = Math.max(finalY + 25, pageHeight - 60); // Position signature section
    
    // Draw signature boxes
    doc.setDrawColor(100, 100, 100);
    doc.setLineWidth(0.5);
    
    // Box dimensions
    const boxWidth = 55;
    const boxHeight = 35;
    const startX = 14;
    const spacing = 8;
    
    // Manager box
    doc.rect(startX, signatureY, boxWidth, boxHeight);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Manager', startX + boxWidth / 2, signatureY + 8, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('Name: ___________________', startX + 2, signatureY + 18);
    doc.text('Designation: _____________', startX + 2, signatureY + 25);
    doc.text('Signature: _______________', startX + 2, signatureY + 32);
    
    // Stock Manager box
    const stockMgrX = startX + boxWidth + spacing;
    doc.rect(stockMgrX, signatureY, boxWidth, boxHeight);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Stock Manager', stockMgrX + boxWidth / 2, signatureY + 8, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('Name: ___________________', stockMgrX + 2, signatureY + 18);
    doc.text('Designation: _____________', stockMgrX + 2, signatureY + 25);
    doc.text('Signature: _______________', stockMgrX + 2, signatureY + 32);
    
    // Checker box
    const checkerX = stockMgrX + boxWidth + spacing;
    doc.rect(checkerX, signatureY, boxWidth, boxHeight);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Checker', checkerX + boxWidth / 2, signatureY + 8, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('Name: ___________________', checkerX + 2, signatureY + 18);
    doc.text('Designation: _____________', checkerX + 2, signatureY + 25);
    doc.text('Signature: _______________', checkerX + 2, signatureY + 32);
    
    // Save PDF
    const dateStr = filters.date ? `_${filters.date}` : '';
    const toDateStr = filters.dateTo ? `_to_${filters.dateTo}` : '';
    doc.save(`Stock_Movement_Report${dateStr}${toDateStr}.pdf`);
    toast.success('PDF exported successfully');
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    toast.success('Logged out successfully!');
    navigate('/login');
  };

  const content = (
    <div style={userType === 'wstoremanager' || userType === 'warehousestoremanager' ? { margin: '20px 20px' } : {}}>
      {/* {(userType === 'wstoremanager' || userType === 'warehousestoremanager') && (
        <WarehouseTopbar
          title="Stock Reports"
          subtitle="Warehouse Stock Movement & Analysis"
          icon={<i className="fa fa-chart-bar"></i>}
        />
      )}
      <Header title="Stock Report" /> */}
      <ToastContainer />
      <div className="row compact-report-ui">
        <div className="col-12">
          <CardComponent title="Stock Report Filters" headerColor="primary">
            {/* Tab Navigation - Closing Stock Report tab removed */}
            <div className="tab-struct custom-tab-1 custom-tabs mb-4">
              <ul role="tablist" className="nav nav-tabs">
                <li className={reportType === 'stock' ? 'active' : ''}>
                  <a
                    href="#stock-movement"
                    onClick={(e) => {
                      e.preventDefault();
                      switchReportType('stock');
                    }}
                    role="tab"
                  >
                    <span className="hidden-xs">
                      <i className="fa fa-exchange"></i> Stock Movement Report
                    </span>
                  </a>
                </li>
              </ul>
            </div>

            {/* Filter Mode Buttons - Only show for Stock Movement Report */}
            {reportType === 'stock' && (
              <div className="filter-mode-buttons">
                <button 
                  className={`filter-mode-btn ${filterMode === 'search' ? 'active' : ''}`}
                  onClick={() => handleFilterModeChange('search')}
                >
                  <i className="fa fa-search"></i>
                  <span>Search</span>
                </button>
                
                <button 
                  className={`filter-mode-btn ${filterMode === 'date' ? 'active' : ''}`}
                  onClick={() => handleFilterModeChange('date')}
                >
                  <i className="fa fa-calendar"></i>
                  <span>Filter by Date</span>
                </button>
                
                <button 
                  className={`filter-mode-btn ${filterMode === 'date-supplier' ? 'active' : ''}`}
                  onClick={() => handleFilterModeChange('date-supplier')}
                >
                  <i className="fa fa-calendar-check-o"></i>
                  <span>Filter by Date & Supplier</span>
                </button>
                
                <button 
                  className={`filter-mode-btn ${filterMode === 'date-sponsor' ? 'active' : ''}`}
                  onClick={() => handleFilterModeChange('date-sponsor')}
                >
                  <i className="fa fa-calendar-plus-o"></i>
                  <span>Filter by Date & Sponsor</span>
                </button>
                
                <button 
                  className={`filter-mode-btn ${filterMode === 'date-item' ? 'active' : ''}`}
                  onClick={() => handleFilterModeChange('date-item')}
                >
                  <i className="fa fa-calendar-o"></i>
                  <span>Filter by Date and Item</span>
                </button>
                
                <button 
                  className={`filter-mode-btn ${filterMode === 'date-item-group' ? 'active' : ''}`}
                  onClick={() => handleFilterModeChange('date-item-group')}
                >
                  <i className="fa fa-bar-chart"></i>
                  <span>Filter By Date and Group by Item</span>
                </button>
                
                <button 
                  className={`filter-mode-btn ${filterMode === 'item-supplier-date' ? 'active' : ''}`}
                  onClick={() => handleFilterModeChange('item-supplier-date')}
                >
                  <i className="fa fa-filter"></i>
                  <span>Filter by Item And Supplier with Date</span>
                </button>
                
                <button 
                  className={`filter-mode-btn ${filterMode === 'invoice-group' ? 'active' : ''}`}
                  onClick={() => handleFilterModeChange('invoice-group')}
                >
                  <i className="fa fa-file-text-o"></i>
                  <span>Group By Invoice No. with Date</span>
                </button>
              </div>
            )}

            {/* Filters Row 1 */}
            <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
              {reportType === 'closingstock' ? (
                /* Only show category filter for closing stock */
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Text strong style={{ display: 'block', marginBottom: '8px', color: '#2c3e50' }}>
                    Category
                  </Text>
                  <Select
                    style={{ width: '100%' }}
                    value={filters.category}
                    onChange={(value) => handleFilterChange({ target: { name: 'category', value } })}
                    placeholder="Select category"
                    size="large"
                  >
                    <Option value="">All Categories</Option>
                    <Option value="Liquer">Liquer</Option>
                    <Option value="Inventory">Inventory</Option>
                    <Option value="Furniture">Furniture</Option>
                    <Option value="Housekeeping">Housekeeping</Option>
                    <Option value="Electronics">Electronics</Option>
                  </Select>
                </Col>
              ) : (
                /* Show filters based on filter mode for stock movement report */
                <>
                  {/* Date Range Picker - always show for stock movement */}
                  <Col xs={24} sm={24} md={12} lg={8}>
                    <Text strong style={{ display: 'block', marginBottom: '8px', color: '#2c3e50' }}>
                      📅 Date Range
                    </Text>
                    <RangePicker
                      value={dateRange}
                      onChange={handleDateRangeChange}
                      format="YYYY-MM-DD"
                      size="large"
                      style={{
                        width: '100%',
                        borderRadius: '8px',
                        border: '2px solid #DAA520',
                        boxShadow: '0 2px 4px rgba(218,165,32,0.2)'
                      }}
                      separator={
                        <span style={{ color: '#DAA520', fontWeight: '700', fontSize: '16px' }}>
                          →
                        </span>
                      }
                      placeholder={['Start Date', 'End Date']}
                      suffixIcon={
                        <CalendarOutlined style={{ color: '#DAA520', fontSize: '16px' }} />
                      }
                    />
                  </Col>

                  {/* Item Name filter - show for item-related modes */}
                  {(filterMode === 'date-item' || filterMode === 'date-item-group' || filterMode === 'item-supplier-date' || filterMode === '') && (
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Text strong style={{ display: 'block', marginBottom: '8px', color: '#2c3e50' }}>
                        Item Name
                      </Text>
                      <Select
                        showSearch
                        style={{ width: '100%' }}
                        value={filters.item_name || undefined}
                        onChange={(value) => handleFilterChange({ target: { name: 'item_name', value: value || '' } })}
                        placeholder="Type or select item..."
                        size="large"
                        filterOption={(input, option) =>
                          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        allowClear
                      >
                        <Option value="">All Items</Option>
                        {getUniqueItems().map((itemName, index) => (
                          <Option key={index} value={itemName}>
                            {itemName}
                          </Option>
                        ))}
                      </Select>
                    </Col>
                  )}

                  {/* Supplier filter - show for supplier-related modes */}
                  {(filterMode === 'date-supplier' || filterMode === 'item-supplier-date' || filterMode === '') && (
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Text strong style={{ display: 'block', marginBottom: '8px', color: '#2c3e50' }}>
                        Supplier Name
                      </Text>
                      <Select
                        showSearch
                        style={{ width: '100%' }}
                        value={filters.supplier_name || undefined}
                        onChange={(value) => handleFilterChange({ target: { name: 'supplier_name', value: value || '' } })}
                        placeholder="Select supplier"
                        size="large"
                        filterOption={(input, option) =>
                          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        allowClear
                      >
                        <Option value="">All Suppliers</Option>
                        {getUniqueSuppliers().map((supplierName, index) => (
                          <Option key={index} value={supplierName}>
                            {supplierName}
                          </Option>
                        ))}
                      </Select>
                    </Col>
                  )}

                  {/* Sponsor filter - show for sponsor mode */}
                  {(filterMode === 'date-sponsor' || filterMode === '') && (
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Text strong style={{ display: 'block', marginBottom: '8px', color: '#2c3e50' }}>
                        Sponsor Name
                      </Text>
                      <Select
                        showSearch
                        style={{ width: '100%' }}
                        value={filters.sponsor_name || undefined}
                        onChange={(value) => handleFilterChange({ target: { name: 'sponsor_name', value: value || '' } })}
                        placeholder="Select sponsor"
                        size="large"
                        filterOption={(input, option) =>
                          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        allowClear
                      >
                        <Option value="">All Sponsors</Option>
                        {getUniqueSponsors().map((sponsorName, index) => (
                          <Option key={index} value={sponsorName}>
                            {sponsorName}
                          </Option>
                        ))}
                      </Select>
                    </Col>
                  )}
                  
                  {/* Category filter - show when no filter mode or invoice-group */}
                  {(filterMode === '' || filterMode === 'invoice-group') && (
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Text strong style={{ display: 'block', marginBottom: '8px', color: '#2c3e50' }}>
                        Category
                      </Text>
                      <Select
                        style={{ width: '100%' }}
                        value={filters.category || undefined}
                        onChange={(value) => handleFilterChange({ target: { name: 'category', value: value || '' } })}
                        placeholder="Select category"
                        size="large"
                        allowClear
                      >
                        <Option value="">All Categories</Option>
                        <Option value="Liquer">Liquer</Option>
                        <Option value="Inventory">Inventory</Option>
                        <Option value="Furniture">Furniture</Option>
                        <Option value="Housekeeping">Housekeeping</Option>
                        <Option value="Electronics">Electronics</Option>
                      </Select>
                    </Col>
                  )}
                </>
              )}
            </Row>

            {/* Filters Row 2 - Only show for Stock Movement when no filter mode is selected */}
            {reportType !== 'closingstock' && filterMode === '' && (
              <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                {/* Stock Type filter */}
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Text strong style={{ display: 'block', marginBottom: '8px', color: '#2c3e50' }}>
                    Stock Type
                  </Text>
                  <Select
                    style={{ width: '100%' }}
                    value={filters.stockType || undefined}
                    onChange={(value) => handleFilterChange({ target: { name: 'stockType', value: value || '' } })}
                    placeholder="Select stock type"
                    size="large"
                    allowClear
                  >
                    <Option value="">All</Option>
                    <Option value="in">Stock In</Option>
                    <Option value="out">Stock Out</Option>
                  </Select>
                </Col>
                
                {/* Ref Number filter - only for Stock Movement */}
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Text strong style={{ display: 'block', marginBottom: '8px', color: '#2c3e50' }}>
                    Ref/Invoice No
                  </Text>
                  <Input
                    name="ref"
                    value={filters.ref}
                    onChange={handleFilterChange}
                    placeholder="Search by ref number..."
                    size="large"
                    prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                    allowClear
                  />
                </Col>
                
                {/* Group by Ref checkbox - only for Stock Movement */}
                <Col xs={24} sm={12} md={8} lg={6}>
                  <div style={{ paddingTop: '32px' }}>
                    <Checkbox
                      name="groupByRef"
                      checked={filters.groupByRef}
                      onChange={(e) => handleFilterChange({ target: { name: 'groupByRef', value: e.target.checked, type: 'checkbox', checked: e.target.checked } })}
                      style={{ fontWeight: '500', color: '#2c3e50' }}
                    >
                      Group by Ref No & Date
                    </Checkbox>
                  </div>
                </Col>
              </Row>
            )}


            {/* Filter Action Buttons Section */}
            <Card 
              title={<span><FilterOutlined style={{ marginRight: 8 }} />Filter Actions</span>}
              style={{ 
                marginTop: '24px',
                marginBottom: '16px',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}
              headStyle={{
                backgroundColor: '#f0f2f5',
                fontWeight: '600',
                fontSize: '16px'
              }}
            >
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <Space size="middle" wrap style={{ width: '100%', justifyContent: 'space-between' }}>
                    {/* Primary Action Buttons */}
                    <Space size="middle" wrap>
                      <Button 
                        type="primary"
                        size="large"
                        icon={<FilterOutlined />}
                        onClick={applyFilters}
                        style={{
                          fontWeight: '700',
                          height: '48px',
                          fontSize: '14px',
                          borderRadius: '6px',
                          boxShadow: '0 2px 8px rgba(24,144,255,0.3)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}
                      >
                        Apply Filters
                      </Button>
                      <Button 
                        size="large"
                        icon={<ReloadOutlined />}
                        onClick={resetFilters}
                        style={{
                          fontWeight: '700',
                          height: '48px',
                          fontSize: '14px',
                          backgroundColor: '#ffc107',
                          borderColor: '#ffc107',
                          color: '#000',
                          borderRadius: '6px',
                          boxShadow: '0 2px 8px rgba(255,193,7,0.3)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#ffb300';
                          e.target.style.borderColor = '#ffb300';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#ffc107';
                          e.target.style.borderColor = '#ffc107';
                        }}
                      >
                        Reset
                      </Button>
                      {reportType === 'stock' && (
                        <>
                          <Button 
                            size="large"
                            icon={<i className="fa fa-arrow-down" style={{ marginRight: '8px' }}></i>}
                            onClick={showStockIn}
                            style={{
                              fontWeight: '700',
                              height: '48px',
                              fontSize: '14px',
                              backgroundColor: '#28a745',
                              borderColor: '#28a745',
                              color: '#fff',
                              borderRadius: '6px',
                              boxShadow: '0 2px 8px rgba(40,167,69,0.3)',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#218838';
                              e.currentTarget.style.borderColor = '#218838';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#28a745';
                              e.currentTarget.style.borderColor = '#28a745';
                            }}
                          >
                            Stock In
                          </Button>
                          <Button 
                            size="large"
                            icon={<i className="fa fa-arrow-up" style={{ marginRight: '8px' }}></i>}
                            onClick={showStockOut}
                            danger
                            style={{
                              fontWeight: '700',
                              height: '48px',
                              fontSize: '14px',
                              borderRadius: '6px',
                              boxShadow: '0 2px 8px rgba(220,53,69,0.3)',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}
                          >
                            Stock Out
                          </Button>
                        </>
                      )}
                    </Space>

                    {/* Hide Checkboxes */}
                    {reportType === 'stock' && (
                      <Space size="middle" wrap>
                        <Checkbox
                          checked={hideClubRows}
                          onChange={(e) => setHideClubRows(e.target.checked)}
                          style={{ 
                            fontWeight: '500',
                            fontSize: '14px',
                            padding: '8px 16px',
                            backgroundColor: '#fff',
                            borderRadius: '6px',
                            border: '1px solid #d9d9d9',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                          }}
                        >
                          Hide Club Supplier Rows
                        </Checkbox>
                        <Checkbox
                          checked={hideClubReturnRows}
                          onChange={(e) => setHideClubReturnRows(e.target.checked)}
                          style={{ 
                            fontWeight: '500',
                            fontSize: '14px',
                            padding: '8px 16px',
                            backgroundColor: '#fff',
                            borderRadius: '6px',
                            border: '1px solid #d9d9d9',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                          }}
                        >
                          Hide Club_Return Supplier Rows
                        </Checkbox>
                      </Space>
                    )}
                    {(reportType === 'closingstock' || viewingClosingStock) && (
                      <Checkbox
                        checked={showZeroClosingStock}
                        onChange={(e) => setShowZeroClosingStock(e.target.checked)}
                        style={{ 
                          fontWeight: '500',
                          fontSize: '14px',
                          padding: '8px 16px',
                          backgroundColor: '#fff',
                          borderRadius: '6px',
                          border: '1px solid #d9d9d9',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                        }}
                      >
                        Show Zero Stock Items
                      </Checkbox>
                    )}
                  </Space>
                </Col>
              </Row>
            </Card>

            {/* Export Buttons Section */}
            <Card 
              title={<span><FileTextOutlined style={{ marginRight: 8 }} />Export Options</span>}
              style={{ 
                marginBottom: '24px',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}
              headStyle={{
                backgroundColor: '#f0f2f5',
                fontWeight: '600',
                fontSize: '16px'
              }}
            >
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>
                    {reportType === 'stock' && !viewingClosingStock && (
                      <Button
                        onClick={exportStockMovementPDF}
                        size="large"
                        icon={<FileTextOutlined />}
                        style={{
                          backgroundColor: '#dc3545',
                          borderColor: '#dc3545',
                          color: '#fff',
                          fontWeight: '700',
                          height: '40px',
                          fontSize: '13px',
                          borderRadius: '6px',
                          boxShadow: '0 2px 6px rgba(220,53,69,0.3)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#c82333';
                          e.currentTarget.style.borderColor = '#c82333';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#dc3545';
                          e.currentTarget.style.borderColor = '#dc3545';
                        }}
                      >
                        Movement PDF
                      </Button>
                    )}
                    <ExportButtons
                      data={getDisplayedData()}
                      filename={`${reportType}_report_${new Date().toISOString().split('T')[0]}`}
                      title={filterMode === 'invoice-group' ? 'Invoice Details' : `${reportType === 'closingstock' ? 'Closing Stock' : 'Stock Movement'} Report`}
                      columns={getExportColumns()}
                      size="sm"
                      pdfOrientation={viewingClosingStock || reportType === 'closingstock' || filterMode === 'invoice-group' ? 'portrait' : 'landscape'}
                      rowStyler={getRowStyleForExport}
                      summaryData={filterMode === 'invoice-group' ? invoiceSummary : null}
                    />
                  </div>
                </Col>
              </Row>
            </Card>
          </CardComponent>
        </div>

        <div className="col-12 mt-4" id="tableid">
          {/* Quick Search Input - Show for both Stock Movement and Closing Stock Reports */}
          <div className="global-search-input mb-3">
            <div className="form-row row">
              <div className="form-group col-md-12 mb-0">
                <label style={{fontWeight: 600, color: '#333', marginBottom: '8px'}}>🔍 Quick Search</label>
                <div className="global-search-wrapper">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Type to search by item, supplier, sponsor, ref, invoice, or category..."
                    value={globalSearch}
                    onChange={handleGlobalSearchChange}
                  />
                 
                </div>
              </div>
            </div>
          </div>

          {filteredData.length === 0 ? (
            <div className="text-center py-5">
              <i className="fa fa-database fa-3x text-muted mb-3"></i>
              <p className="text-muted">No data available for the selected filters.</p>
            </div>
          ) : (
            <Card
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '18px', fontWeight: '700', letterSpacing: '0.5px' }}>
                    <BarChartOutlined style={{ marginRight: '8px', color: '#FFD700' }} />
                    {`${viewingClosingStock || reportType === 'closingstock' ? 'Closing Stock' : 'Stock Movement'} Report`}
                  </span>
                  <Tag color="gold" style={{ fontSize: '14px', fontWeight: '500', padding: '6px 16px', borderRadius: '20px' }}>
                    {filteredData.length} Records
                  </Tag>
                </div>
              }
              style={{ 
                borderRadius: '12px', 
                overflow: 'hidden', 
                boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                border: '2px solid #e3f2fd',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
              }}
              headStyle={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff'
              }}
            >
              <Table
                dataSource={getDisplayedData().map((row, index) => ({
                  ...row,
                  key: row.id || row.inv || row.ref || `row-${index}`,
                  rowIndex: index
                }))}
                columns={columns.map(col => {
                  const columnConfig = {
                    title: col.label,
                    dataIndex: col.field,
                    key: col.field,
                    align: (col.field === 'item_name' || col.field === 'name') ? 'left' : 'center',
                    width: col.field === 'actions' ? 180 : undefined,
                    render: (text, record) => {
                      const isZeroStock = (viewingClosingStock || reportType === 'closingstock') && parseFloat(record.quantity || 0) === 0;
                      
                      if (col.field === 'actions') {
                        return (
                          <Space size="small">
                            <Button
                              type="primary"
                              icon={<i className="fa fa-edit"></i>}
                              size="small"
                              onClick={() => handleEdit(record)}
                              style={{
                                backgroundColor: '#17a2b8',
                                borderColor: '#17a2b8',
                                fontWeight: '600'
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              danger
                              icon={<i className={deleteConfirm === record ? 'fa fa-exclamation-triangle' : 'fa fa-trash'}></i>}
                              size="small"
                              onClick={() => handleDelete(record)}
                              style={{
                                fontWeight: '600',
                                backgroundColor: deleteConfirm === record ? '#c82333' : undefined
                              }}
                            >
                              {deleteConfirm === record ? 'Confirm' : 'Delete'}
                            </Button>
                          </Space>
                        );
                      }
                      
                      if ((col.field === 'date' || col.field === 'invoice_date') && text) {
                        return new Date(text).toLocaleDateString();
                      }
                      
                      const moneyFields = ['rate', 'amount', 'vatamount', 'subtotal', 'netAmount', 'taxAmount', 
                                          'purchase_price', 'price', 'total_amount', 'total_vat_amount', 'grand_total', 'closing_stock'];
                      if (moneyFields.includes(col.field)) {
                        return (
                          <Text strong style={{ color: isZeroStock ? '#fff' : '#52c41a' }}>
                            ฿{parseFloat(text || 0).toFixed(2)}
                          </Text>
                        );
                      }
                      
                      const numberFields = ['qty', 'sold_qty', 'opening_stock', 'stock_in', 'stock_out', 
                                           'ostock', 'quantity', 'total_items', 'categories', 'unique_products', 
                                           'total_quantity', 'total_sold', 'total'];
                      if (numberFields.includes(col.field)) {
                        return (
                          <Text strong style={{ color: isZeroStock ? '#fff' : '#1890ff' }}>
                            {text || 0}
                          </Text>
                        );
                      }
                      
                      if (col.field === 'total_vat') {
                        return (
                          <Text strong style={{ color: isZeroStock ? '#fff' : '#13c2c2' }}>
                            {parseFloat(text || 0).toFixed(2)}%
                          </Text>
                        );
                      }
                      
                      return text || '-';
                    }
                  };
                  return columnConfig;
                })}
                pagination={{
                  current: currentPage,
                  pageSize: itemsPerPage,
                  total: displayedData.length,
                  onChange: (page) => setCurrentPage(page),
                  showSizeChanger: false,
                  showTotal: (total, range) => `Showing ${range[0]} to ${range[1]} of ${total} entries`,
                  position: ['bottomCenter'],
                  style: { marginTop: '16px' }
                }}
                scroll={{ x: 'max-content', y: 600 }}
                size="small"
                bordered
                rowClassName={(record) => {
                  const isZeroStock = (viewingClosingStock || reportType === 'closingstock') && parseFloat(record.quantity || 0) === 0;
                  return isZeroStock ? 'zero-stock-row' : '';
                }}
                footer={() => {
                  if ((reportType === 'stock' && !viewingClosingStock) || (viewingClosingStock || reportType === 'closingstock')) {
                    const totalAmount = filterMode === 'invoice-group' 
                      ? filteredData.reduce((acc, item) => acc + parseFloat(item.total_amount || 0), 0)
                      : (viewingClosingStock || reportType === 'closingstock')
                        ? filteredData.reduce((acc, item) => acc + parseFloat(item.total_amount || 0), 0)
                        : filteredData.reduce((acc, item) => acc + parseFloat(item.amount || 0), 0);
                    
                    return (
                      <div style={{ 
                        textAlign: 'right', 
                        fontWeight: '700', 
                        fontSize: '16px',
                        padding: '12px',
                        backgroundColor: '#f0f2f5',
                        borderTop: '2px solid #d9d9d9'
                      }}>
                        <Text strong style={{ marginRight: '16px' }}>Total Amount:</Text>
                        <Text strong style={{ color: '#52c41a', fontSize: '18px' }}>
                          ฿{totalAmount.toFixed(2)}
                        </Text>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <style>{`
                .zero-stock-row {
                  background-color: #e74c3c !important;
                  color: white !important;
                  font-weight: 500;
                }
                .zero-stock-row:hover {
                  background-color: #c0392b !important;
                }
                .zero-stock-row td {
                  color: white !important;
                }
                .ant-table-tbody > tr:hover:not(.zero-stock-row) > td {
                  background-color: #d5dbdb !important;
                }
              `}</style>
            </Card>
          )}  
        </div>
      </div>
    </div>
  );

  return userType === 'wstoremanager' || userType === 'warehousestoremanager' ? (
    <Layout>
      <>
        {/* Floating Home Button */}
        <Button
          type="primary"
          icon={<HomeOutlined />}
          shape="circle"
          size="large"
          style={{ 
            position: 'fixed',
            right: 24, 
            bottom: 100,
            width: 56,
            height: 56,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            backgroundColor: '#8B4513',
            borderColor: '#8B4513'
          }}
          onClick={() => navigate('/dashboard/warehousestoremanager')}
          title="Back to Dashboard"
        />
        
        {/* Floating Logout Button */}
        <Button
          type="primary"
          icon={<LogoutOutlined />}
          shape="circle"
          size="large"
          danger
          style={{ 
            position: 'fixed',
            right: 24, 
            bottom: 24,
            width: 56,
            height: 56,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000
          }}
          onClick={handleLogout}
          title="Logout"
        />
        {content}
      </>
    </Layout>
  ) : (
    <Layout>{content}</Layout>
  );
}
